/**
 * @enum {string}
 * @readonly
 **/
export const ACCESS_AGENT = {
    MOBILE_WEB: 'MOBILE_WEB',
    MOBILE_APP: 'MOBILE_APP',
    MOBILE_PAY_APP: 'PAY_APP',
    /** 네이버 쇼핑앱 에이전트 */
    NAVER_SHOPPING_APP: 'NAVER_SHOPPING_APP',
    PC_WEB: 'PC_WEB',
} as const

export type AccessAgent = (typeof ACCESS_AGENT)[keyof typeof ACCESS_AGENT]

type MobileAppType = 'NAVER' | 'NAVER_PAY' | 'NAVER_SHOPPING'

/**
 * ACCESS_AGENT 기반 네이버앱 분류
 * - 식별할 수 없는 agent 인 경우, undefined.
 */
export const MOBILE_APP_TYPE: Record<(typeof ACCESS_AGENT)[keyof typeof ACCESS_AGENT], MobileAppType | undefined> = {
    /** 네이버 앱 */
    [ACCESS_AGENT.MOBILE_APP]: 'NAVER',
    /** 네이버 페이 앱 */
    [ACCESS_AGENT.MOBILE_PAY_APP]: 'NAVER_PAY',
    /** 네이버 쇼핑앱 */
    [ACCESS_AGENT.NAVER_SHOPPING_APP]: 'NAVER_SHOPPING',
    [ACCESS_AGENT.MOBILE_WEB]: undefined,
    [ACCESS_AGENT.PC_WEB]: undefined,
}

// Android: nApps (Android OS 8.0.0;LGM-V300S;NaverPay;1.0.0;GDOT)
// iOS(iOS, iPadOS 14 이하): nApps (iOS 13.3;iPhone9,3;NaverPay;1.0.0;GDOT)
// iPadOS(iPadOS 15 이상): nApps (iPadOS 15.2.1;iPad8,9;NaverPay;1.6.0)
export const PAY_APP_NATIVE_AGENT = /nApps ?\((Android|iOS|iPadOS).+;.+;NaverPay;.+\)/i

// Android : nApps (Android OS 8.0.0;LGM-V300S;CrosswalkSearch;10.0.3;GDOT)
// iOS : nApps (iOS 12.0.1;iPhone9,3;NaverSearch;10.0.0;GDOT)
export const NAVER_APP_NATIVE_AGENT = /nApps ?\((Android|iOS).+;.+;(NaverSearch|CrosswalkSearch);.+\)/i

// iOS(iOS, iPadOS 14 이하): nApps (iOS 13.3;iPhone9,3;NaverPay;1.0.0;GDOT)
// iPadOS(iPadOS 15 이상): nApps (iPadOS 15.2.1;iPad8,9;NaverPay;1.6.0)
export const PAY_APP_IOS_NATIVE_AGENT = /nApps ?\((iOS|iPadOS).+;.+;NaverPay;.+\)/i

// Android: nApps (Android OS 8.0.0;LGM-V300S;NaverPay;1.0.0;GDOT)
export const PAY_APP_ANDROID_NATIVE_AGENT = /nApps ?\(Android.+;.+;NaverPay;.+\)/i

// iOS : nApps (iOS 12.0.1;iPhone9,3;NaverSearch;10.0.0;GDOT)
export const NAVER_APP_IOS_NATIVE_AGENT = /nApps ?\(iOS.+;.+;(NaverSearch|CrosswalkSearch);.+\)/i

// Android : nApps (Android OS 8.0.0;LGM-V300S;CrosswalkSearch;10.0.3;GDOT)
export const NAVER_APP_ANDROID_NATIVE_AGENT = /nApps ?\(Android.+;.+;(NaverSearch|CrosswalkSearch);.+\)/i

// nApps (watchOS 8.4.2;Watch6,9;NaverPayWatch;1.10.0.5)
export const PAY_APP_WATCH_OS_NATIVE_AGENT = /nApps ?\(watchOS.+;.+;NaverPayWatch;.+\)/i

// nApps (Android OS 11;SM-R865N;NaverPayWatch;1.10.0-qa05)
export const PAY_APP_WEAR_OS_NATIVE_AGENT = /nApps ?\(Android.+;.+;NaverPayWatch;.+\)/i

// nApps (WatchOS 7;Watch7,1;NaverSearchWatch;11.12.5)
export const NAVER_APP_WATCH_OS_NATIVE_AGENT = /nApps ?\(WatchOS.+;.+;NaverSearchWatch;.+\)/i

// nApps (Android OS 11;SM-R865N;NaverSearchWatch;1.1.0)
export const NAVER_APP_WEAR_OS_NATIVE_AGENT = /nApps ?\(Android.+;.+;NaverSearchWatch;.+\)/i

export const PAY_APP_AGENT_REGEX = /NAVER\([inapp|higgs]+;[^0-9]*pay;[^0-9]*(\d+);[^0-9]*(\d+.\d+.\d+).*\)/i
export const NAVER_APP_AGENT_REGEX = /NAVER\([inapp|higgs]+;[^0-9]*search;[^0-9]*(\d+);[^0-9]*(\d+.\d+.\d+).*\)/i

/** 네이버 쇼핑 앱 */
export const SHOPPING_APP_WEBVIEW_AGENT_REGEX =
    /NAVER\([inapp|higgs]+;[^0-9]*navershopping;[^0-9]*(\d+);[^0-9]*(\d+.\d+.\d+).*\)/i
/** 네이버 쇼핑 앱(인앱브라우저) */
export const SHOPPING_APP_BROWSER_AGENT_REGEX =
    /NAVER\([inapp|higgs]+;[^0-9]*navershoppingbrowser;[^0-9]*(\d+);[^0-9]*(\d+.\d+.\d+).*\)/i

/** 네이버 쇼핑 앱(별도브라우저) */
export const SHOPPING_APP_EXTRA_BROWSER_AGENT_REGEX =
    /NAVER\([inapp|higgs]+;[^0-9]*shopping-sdk;[^0-9]*(\d+);[^0-9]*(\d+.\d+.\d+).*\)/i

export const APP_VERSION_NATIVE_REGEX = /nApps \(*.+;.+;*;[^0-9]*(\d+.\d+.\d+).*\)/i
export const APP_VERSION_AGENT_REGEX = /NAVER\(*.+;.+;*;[^0-9]*(\d+.\d+.\d+).*\)/i
